import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';

const SearchSection = ({ onSearch, searchQuery, setSearchQuery }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Popular search terms and suggestions
  const popularSearches = [
    'Créer une campagne',
    'Configurer les commissions',
    'Générer des liens',
    'Analyser les performances',
    'Gérer les partenaires',
    'API documentation',
    'Paramètres de compte',
    'Résoudre les problèmes'
  ];

  const searchSuggestions = [
    { term: 'campagne', results: 12, category: 'Guides' },
    { term: 'commission', results: 8, category: 'FAQ' },
    { term: 'partenaire', results: 15, category: 'Tutoriels' },
    { term: 'analytics', results: 6, category: 'Documentation' },
    { term: 'API', results: 10, category: 'Technique' },
    { term: 'liens', results: 7, category: 'Guides' }
  ];

  useEffect(() => {
    if (searchQuery?.length > 1) {
      const filtered = searchSuggestions?.filter(suggestion => 
        suggestion?.term?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      );
      setSuggestions(filtered);
      setShowSuggestions(true);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [searchQuery]);

  const handleSearchSubmit = (e) => {
    e?.preventDefault();
    if (searchQuery?.trim()) {
      onSearch(searchQuery);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (term) => {
    setSearchQuery(term);
    onSearch(term);
    setShowSuggestions(false);
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
          <Icon name="Search" size={20} className="text-accent" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-card-foreground">Recherche Intelligente</h2>
          <p className="text-sm text-muted-foreground">
            Trouvez rapidement les informations que vous cherchez
          </p>
        </div>
      </div>
      {/* Search Form */}
      <form onSubmit={handleSearchSubmit} className="relative">
        <div className="relative">
          <Icon name="Search" size={20} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Rechercher dans la documentation... (ex: créer une campagne, API, commissions)"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e?.target?.value)}
            className="pl-12 pr-4 py-3 text-base"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => { setSearchQuery(''); setShowSuggestions(false); }}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <Icon name="X" size={16} />
            </button>
          )}
        </div>

        {/* Search Suggestions Dropdown */}
        {showSuggestions && suggestions?.length > 0 && (
          <div className="absolute top-full left-0 right-0 z-10 bg-card border border-border rounded-lg shadow-lg mt-2">
            <div className="p-4">
              <h3 className="text-sm font-medium text-card-foreground mb-3">Suggestions</h3>
              <div className="space-y-2">
                {suggestions?.map((suggestion, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleSuggestionClick(suggestion?.term)}
                    className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-muted rounded-lg transition-colors"
                  >
                    <div className="flex items-center space-x-3">
                      <Icon name="Search" size={16} className="text-muted-foreground" />
                      <span className="text-sm text-card-foreground">{suggestion?.term}</span>
                      <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
                        {suggestion?.category}
                      </span>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {suggestion?.results} résultats
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </form>
      {/* Popular Searches */}
      <div className="mt-6">
        <h3 className="text-sm font-medium text-card-foreground mb-3">Recherches populaires</h3>
        <div className="flex flex-wrap gap-2">
          {popularSearches?.map((search, index) => (
            <button
              key={index}
              onClick={() => handleSuggestionClick(search)}
              className="px-3 py-1.5 bg-muted text-muted-foreground text-xs rounded-full hover:bg-accent hover:text-accent-foreground transition-colors"
            >
              {search}
            </button>
          ))}
        </div>
      </div>
      {/* Search Tips */}
      <div className="mt-6 p-4 bg-muted/50 rounded-lg">
        <div className="flex items-start space-x-3">
          <Icon name="Lightbulb" size={16} className="text-warning flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-card-foreground mb-1">Conseils de recherche</h4>
            <ul className="text-xs text-muted-foreground space-y-1">
              <li>• Utilisez des mots-clés spécifiques comme "créer campagne" ou "analyser performances"</li>
              <li>• Les recherches sont insensibles à la casse et aux accents</li>
              <li>• Essayez différentes formulations si vous ne trouvez pas ce que vous cherchez</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchSection;